import "@asset/style/index.scss";
import { hydrateRoot } from "react-dom/client";
import { i18nInstance as i18n, initClientI18n } from "@core/i18n/client-config";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "@lib/createEmotionCache";
import ThemeProvider from "@core/context/theme-context";
import { getClientTheme } from "@core/theme/client-theme";
import * as Sentry from "@sentry/react";

const isProd = process.env.NODE_ENV === "production";

const getInitialLanguage = (): string => {
  if (window.__I18N_DATA__?.lang) return window.__I18N_DATA__.lang;
  return window.location.pathname.startsWith("/zh") ? "zh-TW" : "en-US";
};

const initializeClient = async () => {
  if (isProd) {
    Sentry.init({
      dsn: "https://1ad6056cb14065aae4c903d460e16f12@o349150.ingest.us.sentry.io/4509206857121792",
      environment: "production",
    });
  }

  const initialLang = getInitialLanguage();
  await initClientI18n(initialLang);

  if (!window.__I18N_DATA__) {
    window.__I18N_DATA__ = {
      lang: navigator.language.startsWith("zh") ? "zh-TW" : "en-US",
      translations: {},
    };
  }

  if (window.__I18N_DATA__?.translations) {
    i18n.addResourceBundle(
      initialLang,
      "common",
      window.__I18N_DATA__.translations,
      true
    );
  }

  i18n.on("languageChanged", (lng) => {
    window.__I18N_DATA__ = {
      lang: lng,
      translations: i18n.getResourceBundle(lng, "common"),
    };
  });

  const cache = createEmotionCache();
  const theme = getClientTheme();

  const { default: I18nClientProvider } = await import("./i18n-client");

  hydrateRoot(
    document.getElementById("app") as HTMLElement,
    <CacheProvider value={cache}>
      <ThemeProvider initialMode={theme}>
        <I18nClientProvider />
      </ThemeProvider>
    </CacheProvider>
  );
};

initializeClient().catch(console.error);
